import { graphql } from "./generated/index.js"

// Operations on the record_sources table and the external systems it points at
// (pipedrive, airtable, mailchimp, luma, hubspot, sanity, standard metrics).

// Unified record_sources ======================================================

// All sources for a record (incl. cached context payload).
export const fetchRecordSources = graphql(`
  query fetchRecordSources($recordId: Int!) {
    sources: record_sources(where: { record_id: { _eq: $recordId } }) {
      id
      source_type
      source_id
      context
      created_at
      enrichment_pending
    }
  }
`)

// The record of a type that holds a source. The uniqueness trigger allows one
// per record type, so a limit of 1 is the whole answer.
export const fetchSourceHolder = graphql(`
  query fetchSourceHolder($sourceType: String!, $sourceId: String!, $recordType: String!) {
    sources: record_sources(
      where: {
        source_type: { _eq: $sourceType }
        source_id: { _eq: $sourceId }
        record: { type: { _eq: $recordType } }
      }
      limit: 1
    ) {
      record_id
    }
  }
`)

// Which source types each of a set of records carries.
export const fetchSourceTypesForRecords = graphql(`
  query fetchSourceTypesForRecords($recordIds: [Int!]!) {
    sources: record_sources(where: { record_id: { _in: $recordIds } }) {
      record_id
      source_type
    }
  }
`)

// The cached context for a (source_type, source_id), for a reader that only
// knows the source, not the record holding it. Newest snapshot wins.
export const fetchSourceContext = graphql(`
  query fetchSourceContext($sourceType: String!, $sourceId: String!) {
    record_sources(
      where: { source_type: { _eq: $sourceType }, source_id: { _eq: $sourceId } }
      order_by: { updated_at: desc_nulls_last }
      limit: 1
    ) {
      id
      context
    }
  }
`)

// Add source to record (optionally with a cached context snapshot).
//
// is_primary is a presentation column. Leave it unset and GraphQL drops the
// object field, so the column default applies (is_primary DEFAULT FALSE);
// passing an explicit null would store NULL over that default instead.
export const addRecordSource = graphql(`
  mutation addRecordSource($object: record_sources_insert_input!) {
    insert_record_sources_one(object: $object) {
      ...RecordSourceFields
    }
  }
`)

// Patch an existing source row's own columns (the cached `context` snapshot).
export const updateRecordSource = graphql(`
  mutation updateRecordSource($id: Int!, $_set: record_sources_set_input!) {
    update_record_sources_by_pk(pk_columns: { id: $id }, _set: $_set) {
      ...RecordSourceFields
    }
  }
`)

export const updateManyRecordSources = graphql(`
  mutation updateManyRecordSources($updates: [record_sources_updates!]!) {
    update_record_sources_many(updates: $updates) {
      affected_rows
      returning {
        ...RecordSourceFields
      }
    }
  }
`)

// Undo a source's import: detach_and_revert_source (postgres/functions/
// detach_and_revert_source.sql) removes the record's claim on the source and
// rolls back the data and description it infilled, leaving later human edits
// alone. Returns the record as it stands after.
export const detachAndRevertSource = graphql(`
  mutation detachAndRevertSource($recordId: Int!, $sourceType: String!, $sourceId: String!) {
    detach_and_revert_source(
      args: { p_record_id: $recordId, p_source_type: $sourceType, p_source_id: $sourceId }
    ) {
      ...RecordFields
    }
  }
`)

export const removeRecordSource = graphql(`
  mutation removeRecordSource($recordId: Int!, $sourceType: String!, $sourceId: String!) {
    delete_record_sources(
      where: {
        record_id: { _eq: $recordId }
        source_type: { _eq: $sourceType }
        source_id: { _eq: $sourceId }
      }
    ) {
      affected_rows
    }
  }
`)

// System-specific source search and lookup ====================================

// Pipedrive =====================================================================

// Airtable ======================================================================

// Mailchimp =====================================================================

// Luma ==========================================================================

// Slack =========================================================================

// HubSpot =======================================================================

// Sanity ========================================================================

// Standard Metrics ==============================================================

// Unlinked-source discovery =====================================================

// Batched add/remove for the reference write path ==============================
// `addRecordSource` above inserts one row at a time and is kept for the
// source-attribution callers that already use it. These two are what
// applyRelationOps needs: one statement per field, whatever the set size.
//
// UNIQUE (record_id, source_type, source_id) again excludes schema_key. An
// unqualified (aggregate) row is ADOPTED into the field being written; a row
// another field already stamped is NOT updated (the `where` excludes it),
// drops out of `returning`, and the count check raises instead of silently
// stealing it.
export const insertManyRecordSources = graphql(`
  mutation insertManyRecordSources($objects: [record_sources_insert_input!]!) {
    insert_record_sources(
      objects: $objects
      on_conflict: {
        constraint: record_sources_record_id_source_type_source_id_unique
        update_columns: [schema_key, schema_id, context]
        where: { schema_key: { _is_null: true } }
      }
    ) {
      returning {
        ...RecordSourceFields
      }
    }
  }
`)

export const deleteSourcesByIds = graphql(`
  mutation deleteSourcesByIds($ids: [Int!]!) {
    delete_record_sources(where: { id: { _in: $ids } }) {
      affected_rows
      returning {
        id
      }
    }
  }
`)
